<script setup>
import { useTemplateRef, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useConversationFilterContext } from 'dashboard/components-next/filter/provider.js';

import Button from 'next/button/Button.vue';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';

const props = defineProps({
  teamId: { type: [String, Number], required: true },
  teamName: { type: String, default: '' },
  hasExistingFilter: { type: Boolean, default: false },
});

const emit = defineEmits(['apply', 'clear', 'close']);

const showRemoveOptions = ref(false);

const filters = defineModel({
  type: Array,
  default: () => [],
});

const { t } = useI18n();
const { filterTypes } = useConversationFilterContext();

const DEFAULT_FILTER = {
  attributeKey: 'status',
  filterOperator: 'equal_to',
  values: [],
  queryOperator: 'and',
};

const filterScope = ref('this_team');

const scopeOptions = computed(() => [
  {
    label: t('CHAT_LIST.TEAM_FILTER.SCOPE_THIS_TEAM'),
    value: 'this_team',
  },
  {
    label: t('CHAT_LIST.TEAM_FILTER.SCOPE_ALL_TEAMS'),
    value: 'all_teams',
  },
]);

const activeScopeLabel = computed(
  () => scopeOptions.value.find(o => o.value === filterScope.value)?.label || ''
);

const resetFilter = () => {
  filters.value = [{ ...DEFAULT_FILTER }];
};

const removeFilter = index => {
  if (filters.value.length === 1) {
    resetFilter();
  } else {
    filters.value.splice(index, 1);
  }
};

const addFilter = () => {
  filters.value.push({ ...DEFAULT_FILTER });
};

const conditionsRef = useTemplateRef('conditionsRef');

const isConditionsValid = () => {
  return conditionsRef.value.every(condition => condition.validate());
};

const submitFilter = () => {
  if (!isConditionsValid()) return;
  emit('apply', filters.value, filterScope.value);
};

const modalTitle = computed(() => {
  return props.teamName
    ? `${t('CHAT_LIST.TEAM_FILTER.MODAL_TITLE')} - ${props.teamName}`
    : t('CHAT_LIST.TEAM_FILTER.MODAL_TITLE');
});

const outsideClickHandler = [
  () => emit('close'),
  { ignore: ['#toggleTeamFilterButton'] },
];
</script>

<template>
  <div
    v-on-click-outside="outsideClickHandler"
    class="z-40 max-w-3xl lg:w-[750px] overflow-visible w-full border border-n-weak bg-n-alpha-3 backdrop-blur-[100px] shadow-lg rounded-xl p-6 grid gap-6"
  >
    <h3 class="text-base font-medium leading-6 text-n-slate-12">
      {{ modalTitle }}
    </h3>
    <ul class="grid gap-4 list-none">
      <template v-for="(filter, index) in filters" :key="index">
        <ConditionRow
          v-if="index === 0"
          ref="conditionsRef"
          :key="`team-filter-${filter.attributeKey}-0`"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          :filter-types="filterTypes"
          :show-query-operator="false"
          @remove="removeFilter(index)"
        />
        <ConditionRow
          v-else
          :key="`team-filter-${filter.attributeKey}-${index}`"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:query-operator="filters[index - 1].queryOperator"
          v-model:values="filter.values"
          show-query-operator
          :filter-types="filterTypes"
          @remove="removeFilter(index)"
        />
      </template>
    </ul>
    <div class="flex gap-2 justify-between">
      <Button sm ghost blue @click="addFilter">
        {{ $t('FILTER.ADD_NEW_FILTER') }}
      </Button>
      <div class="flex items-center gap-2">
        <div v-if="hasExistingFilter" class="relative">
          <Button sm faded ruby @click="showRemoveOptions = !showRemoveOptions">
            {{ t('CHAT_LIST.TEAM_FILTER.REMOVE_BUTTON') }}
          </Button>
          <div
            v-if="showRemoveOptions"
            class="absolute bottom-full left-0 mb-1 z-50 min-w-[200px] rounded-lg border border-n-weak bg-n-background shadow-lg py-1"
          >
            <button
              class="w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="emit('clear', 'this_team')"
            >
              {{ t('CHAT_LIST.TEAM_FILTER.REMOVE_THIS_TEAM') }}
            </button>
            <button
              class="w-full px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
              @click="emit('clear', 'all_teams')"
            >
              {{ t('CHAT_LIST.TEAM_FILTER.REMOVE_ALL_TEAMS') }}
            </button>
          </div>
        </div>
        <Button v-else sm faded slate @click="resetFilter">
          {{ t('CHAT_LIST.TEAM_FILTER.CLEAR_BUTTON') }}
        </Button>
        <div class="flex items-center gap-1.5">
          <label class="text-xs text-n-slate-11 whitespace-nowrap">
            {{ t('CHAT_LIST.TEAM_FILTER.SCOPE_LABEL') }}
          </label>
          <select
            v-model="filterScope"
            class="text-xs rounded-lg border border-n-weak bg-n-alpha-2 px-2 py-1.5 text-n-slate-12 outline-none"
          >
            <option
              v-for="option in scopeOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <Button sm solid blue @click="submitFilter">
          {{ t('CHAT_LIST.TEAM_FILTER.SAVE_BUTTON') }}
        </Button>
      </div>
    </div>
  </div>
</template>
